import {useLingui} from '@lingui/react';
import {ChargebackDetailsOutput} from '@zentact/api/src/trpc/routers/chargebackRouter';
import {CurrencyCode, LocaleCode, formatAmount, formatLocaleDate} from '@zentact/common';
import {FlatPillWithDot, QuestionTooltip} from '../../elements';
import {
  chargebackStatusToColor,
  displayChargebackTypeMap,
  getChargebackStatusDescriptionMap,
  getChargebackStatusMap,
} from './chargeback-list/columns';

type ChargebackDetailsProps = {
  chargebackRow: ChargebackDetailsOutput | null;
};

export const ChargebackInfo = ({chargebackRow}: ChargebackDetailsProps) => {
  const {i18n} = useLingui();

  if (!chargebackRow) {
    return null;
  }
  const {
    type,
    status,
    pspReferenceId,
    amount,
    currency,
    autoDefended,
    defendable,
    reasonCode,
    reason,
    defensePeriodEndsAt,
    createdAt,
    updatedAt,
  } = chargebackRow;

  const formattedAmount = formatAmount(amount, i18n.locale as LocaleCode, currency as CurrencyCode);

  return (
    <div className="px-4 border-gray-100 sm:px-0">
      <dl className="divide-y divide-gray-100">
        <div className="pb-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">{i18n._('Dispute ID')}</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {pspReferenceId}
          </dd>
        </div>
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">{i18n._('Amount')}</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {formattedAmount}
          </dd>
        </div>
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">{i18n._('Type')}</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {i18n._(displayChargebackTypeMap[type]) ?? i18n._(type)}
          </dd>
        </div>
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">{i18n._('Status')}</dt>
          <dd className="flex items-center mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            <FlatPillWithDot
              color={chargebackStatusToColor[status] || 'blue'}
              label={getChargebackStatusMap(i18n)[status] ?? status}
            />
            <QuestionTooltip
              className="ml-1"
              text={getChargebackStatusDescriptionMap(i18n)[status] ?? status}
            />
          </dd>
        </div>
        {type !== 'NOTIFICATION_OF_FRAUD' && (
          <>
            <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                {i18n._('Auto Defended')}
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                {autoDefended ? i18n._('Yes') : i18n._('No')}
              </dd>
            </div>
            {!autoDefended && (
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  {i18n._('Defendable')}
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {defendable ? i18n._('Yes') : i18n._('No')}
                </dd>
              </div>
            )}
          </>
        )}

        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">{i18n._('Reason Code')}</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {reasonCode}
          </dd>
        </div>
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">
            {i18n._('Reason Message')}
          </dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{reason}</dd>
        </div>
        {defensePeriodEndsAt && (
          <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
            <dt className="text-sm font-medium leading-6 text-gray-900">
              {i18n._('Defense End Date')}
            </dt>
            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {formatLocaleDate(defensePeriodEndsAt)}
            </dd>
          </div>
        )}
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">{i18n._('Last updated')}</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {formatLocaleDate(updatedAt)}
          </dd>
        </div>
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 ">
          <dt className="text-sm font-medium leading-6 text-gray-900">{i18n._('Created')}</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {formatLocaleDate(createdAt)}
          </dd>
        </div>
        <div />
      </dl>
    </div>
  );
};
